<script setup lang="ts">
import { computed, ref } from "vue";
import type { TicketOrderLine } from "@/types";

const props = withDefaults(
  defineProps<{
    lines: TicketOrderLine[];
    totalPrice: number;
    discountAmount?: number;
    totalQuantity: number;
    formatPrice: (value: number) => string;
    actionLabel: string;
    loadingLabel?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
  }>(),
  {
    discountAmount: 0,
    loadingLabel: "",
    isLoading: false,
    isDisabled: false,
  },
);

const emit = defineEmits<{
  action: [];
}>();

const isOpen = ref(false);

const orderCountLabel = computed(() => {
  const value = props.totalQuantity;
  const mod10 = value % 10;
  const mod100 = value % 100;

  if (mod10 === 1 && mod100 !== 11) return `${value} билет`;
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
    return `${value} билета`;
  }

  return `${value} билетов`;
});

function toggle() {
  isOpen.value = !isOpen.value;
}
</script>

<template>
  <div class="fixed inset-x-0 bottom-0 z-50 xl:hidden">
    <aside
      class="mx-auto w-full max-w-[520px] rounded-t-[28px] bg-white p-5 shadow-widget"
    >
      <button
        class="flex w-full cursor-pointer items-center justify-between gap-3 text-left"
        type="button"
        :aria-expanded="isOpen"
        @click="toggle"
      >
        <span class="text-base font-medium leading-5 text-ink">
          Ваш заказ
          <span class="text-ink/50">({{ orderCountLabel }})</span>
        </span>
        <svg
          class="h-5 w-5 min-w-5 text-ink/65 transition-transform"
          :class="isOpen ? '-rotate-90' : 'rotate-90'"
          aria-hidden="true"
        >
          <use href="#ticket-widget-icon-arrow-left" />
        </svg>
      </button>

      <div
        v-if="isOpen"
        class="mt-4 max-h-[38dvh] overflow-y-auto overscroll-contain pr-1"
      >
        <div v-if="lines.length" class="grid gap-3">
          <div
            v-for="line in lines"
            :key="line.ticket.id"
            class="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2"
          >
            <span
              class="min-w-0 overflow-hidden text-xs font-normal leading-4 text-ink [overflow-wrap:anywhere] after:ml-1.5 after:mr-[-100%] after:inline-block after:w-full after:border-b after:border-dashed after:border-ink/20 after:content-['']"
            >
              {{ line.ticket.title }}
              <span class="whitespace-nowrap">{{ line.quantity }} шт.</span>
            </span>
            <span
              class="whitespace-nowrap text-sm font-semibold leading-[18px] text-ink"
            >
              {{ formatPrice(line.amount) }}
            </span>
          </div>
        </div>

        <p v-else class="text-sm font-medium leading-[18px] text-ink/80">
          Выберите нужные тарифы для заказа
        </p>

        <div
          v-if="discountAmount > 0"
          class="mt-3 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 text-darkred"
        >
          <span
            class="min-w-0 overflow-hidden text-xs font-normal leading-4 after:ml-1.5 after:mr-[-100%] after:inline-block after:w-full after:border-b after:border-dashed after:border-ink/20 after:content-['']"
          >
            Скидка по промокоду
          </span>
          <span class="whitespace-nowrap text-sm font-semibold leading-[18px]">
            {{ formatPrice(discountAmount) }}
          </span>
        </div>
      </div>

      <div class="mt-2.5 mb-3 h-px bg-ink/10" />

      <div class="flex items-center justify-between gap-4">
        <span class="text-lg font-semibold leading-[22px] text-ink">Итого</span>
        <span class="text-2xl font-semibold leading-7 text-brand">
          {{ formatPrice(totalPrice) }}
        </span>
      </div>

      <button
        class="mt-3 h-[44px] w-full cursor-pointer rounded-full bg-brand px-6 text-base font-semibold leading-5 text-white transition hover:bg-brand/90 disabled:cursor-default disabled:bg-brand/35"
        type="button"
        :disabled="isDisabled || isLoading"
        @click="emit('action')"
      >
        {{ isLoading && loadingLabel ? loadingLabel : actionLabel }}
      </button>
    </aside>
  </div>
</template>
